import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  ClipboardList,
  Image,
  Settings,
  Shield,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

const managementCards = [
  {
    title: 'Global Settings',
    description: 'App downloads, MQTT, Sonos, and display message settings',
    href: '/admin/settings',
    icon: Settings,
  },
  {
    title: 'Manage Entries',
    description: 'Review, replay, archive, react to, and delete pings',
    href: '/admin/entries',
    icon: ClipboardList,
  },
  {
    title: 'Push',
    description: 'Registered devices and manual push notifications',
    href: '/push',
    icon: Bell,
  },
  {
    title: 'Manage Users',
    description: 'Allowed accounts and access to Ping',
    href: '/admin/users',
    icon: Users,
  },
  {
    title: 'Manage Galleries',
    description: 'External gallery targets and MQTT destinations',
    href: '/admin/galleries',
    icon: Image,
  },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && (!user || user.authType !== 'google' || user.role !== 'admin')) {
      navigate('/login?redirect=/admin');
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user || user.authType !== 'google' || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Redirecting to login...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/share">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Shield className="w-5 h-5 text-primary" />
          <h1 className="font-display font-semibold flex-1">Admin Panel</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4">
        <section className="grid gap-3 sm:grid-cols-2">
          {managementCards.map((card) => {
            const CardIcon = card.icon;

            return (
              <div
                key={card.title}
                className="rounded-xl border border-border bg-card p-4 flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CardIcon className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold">{card.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{card.description}</p>
                  <Link to={card.href} className="inline-flex mt-4">
                    <Button variant="secondary" size="sm">
                      Manage
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </section>
      </main>
    </div>
  );
}
